import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { TabBar } from "../components/TabBar";
import { api } from "../api/client";
import { useExerciseLibrary } from "../hooks/useExerciseLibrary";
import type {
  Exercise,
  ProgramDetail,
  ProgramView,
  ProgressSummary,
  ScheduleEntry,
  WorkoutSession,
  WorkoutTemplate,
} from "../api/types";

interface TodayResp {
  scheduled: ScheduleEntry | null;
  template?: WorkoutTemplate | null;
}

export const Dashboard = () => {
  const [today, setToday] = useState<TodayResp | null>(null);
  const [summary, setSummary] = useState<ProgressSummary | null>(null);
  const [active, setActive] = useState<WorkoutSession | null>(null);
  const [templates, setTemplates] = useState<WorkoutTemplate[]>([]);
  const [programs, setPrograms] = useState<ProgramView[]>([]);
  const [activeProgram, setActiveProgram] = useState<ProgramDetail | null>(null);
  const [picker, setPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      const [t, s, sessions, tpls, progs, ap] = await Promise.all([
        api<TodayResp>("/schedule/today"),
        api<ProgressSummary>("/progress/summary"),
        api<WorkoutSession[]>("/sessions"),
        api<WorkoutTemplate[]>("/templates"),
        api<ProgramView[]>("/programs"),
        api<ProgramDetail | null>("/programs/active"),
      ]);
      setToday(t);
      setSummary(s);
      setTemplates(tpls);
      setPrograms(progs);
      setActiveProgram(ap);
      const a = sessions.find((x) => x.status === "active") ?? null;
      setActive(a);
    })().catch(() => {
      /* ignore */
    });
  }, []);

  const startFromTemplate = async (templateId: string, programId?: string) => {
    setBusy(true);
    try {
      const sess = await api<WorkoutSession>("/sessions", {
        method: "POST",
        body: { templateId, ...(programId ? { programId } : {}) },
      });
      nav(`/session/${sess.id}`);
    } finally {
      setBusy(false);
    }
  };

  const startFromExercise = async (exerciseId: string, name: string) => {
    setBusy(true);
    try {
      const sess = await api<WorkoutSession>("/sessions", {
        method: "POST",
        body: { name, exercises: [{ exerciseId, targetSets: 3 }] },
      });
      nav(`/session/${sess.id}`);
    } finally {
      setBusy(false);
    }
  };

  const startProgramNext = async (p: ProgramView) => {
    if (p.plans.length === 0) return;
    const idx = p.state.currentIndex % p.plans.length;
    await startFromTemplate(p.plans[idx], p.id);
  };

  return (
    <>
      <div className="page">
        <h1 className="h1">Today</h1>
        <div className="muted">
          {new Date().toLocaleDateString(undefined, {
            weekday: "long",
            month: "short",
            day: "numeric",
          })}
        </div>

        {active && (
          <div className="banner" style={{ marginTop: 16 }}>
            <div className="row">
              <div>
                <div>Workout in progress</div>
                <div style={{ fontWeight: 400, fontSize: 13 }}>
                  {active.name ?? "Active workout"}
                </div>
              </div>
              <div className="spacer" />
              <Link className="btn small" to={`/session/${active.id}`}>
                Resume
              </Link>
            </div>
          </div>
        )}

        <div className="col gap-16" style={{ marginTop: 16 }}>
          {activeProgram?.next?.template ? (
            <div className="card">
              <div className="muted">
                Next in <strong>{activeProgram.name}</strong> ({activeProgram.next.index + 1}/{activeProgram.plans.length})
              </div>
              <div className="h2" style={{ marginTop: 0 }}>
                {activeProgram.next.template.name}
              </div>
              <div className="muted" style={{ marginBottom: 12 }}>
                {activeProgram.next.template.exercises.length} exercises
              </div>
              <button
                className="btn"
                disabled={busy}
                onClick={() =>
                  void startFromTemplate(activeProgram.next!.templateId, activeProgram.id)
                }
              >
                Start workout
              </button>
            </div>
          ) : today?.template ? (
            <div className="card">
              <div className="muted">Planned</div>
              <div className="h2" style={{ marginTop: 0 }}>
                {today.template.name}
              </div>
              <div className="muted" style={{ marginBottom: 12 }}>
                {today.template.exercises.length} exercises
              </div>
              <button
                className="btn"
                disabled={busy}
                onClick={() => void startFromTemplate(today.template!.id)}
              >
                Start workout
              </button>
            </div>
          ) : (
            <div className="card">
              <div className="muted">No program active and no workout scheduled today</div>
              <div className="row" style={{ marginTop: 12, gap: 8 }}>
                <Link to="/programs" className="btn secondary">
                  Programs
                </Link>
                <button className="btn" disabled={busy} onClick={() => setPicker(true)}>
                  Quick start
                </button>
              </div>
            </div>
          )}

          {activeProgram && (
            <div className="row" style={{ gap: 8 }}>
              <button className="btn secondary" onClick={() => setPicker(true)}>
                Pick something else
              </button>
            </div>
          )}

          <div className="summary-grid">
            <div className="card">
              <div className="muted">This week</div>
              <div className="big">{summary?.thisWeek ?? "–"}</div>
              <div className="muted">workouts</div>
            </div>
            <div className="card">
              <div className="muted">Streak</div>
              <div className="big">{summary?.streak ?? "–"}</div>
              <div className="muted">days</div>
            </div>
            <div className="card">
              <div className="muted">Total sessions</div>
              <div className="big">{summary?.totalSessions ?? "–"}</div>
              <div className="muted">all time</div>
            </div>
            <div className="card">
              <div className="muted">Volume</div>
              <div className="big">
                {summary ? Math.round(summary.totalVolume / 1000) + "k" : "–"}
              </div>
              <div className="muted">lbs (or kg) × reps</div>
            </div>
          </div>
        </div>
      </div>

      {picker && (
        <QuickStartSheet
          templates={templates}
          programs={programs}
          busy={busy}
          onClose={() => setPicker(false)}
          onPickTemplate={(id) => void startFromTemplate(id)}
          onPickProgram={(p) => void startProgramNext(p)}
          onPickExercise={(ex) => void startFromExercise(ex.id, ex.name)}
        />
      )}

      <TabBar />
    </>
  );
};

type QuickTab = "programs" | "plans" | "exercise";

const QuickStartSheet = ({
  templates,
  programs,
  busy,
  onClose,
  onPickTemplate,
  onPickProgram,
  onPickExercise,
}: {
  templates: WorkoutTemplate[];
  programs: ProgramView[];
  busy: boolean;
  onClose: () => void;
  onPickTemplate: (id: string) => void;
  onPickProgram: (p: ProgramView) => void;
  onPickExercise: (ex: Exercise) => void;
}) => {
  const [tab, setTab] = useState<QuickTab>(programs.length > 0 ? "programs" : "plans");
  const [q, setQ] = useState("");
  const { data: library } = useExerciseLibrary();
  const tplName = (id: string) => templates.find((t) => t.id === id)?.name ?? id;

  const filteredExercises = useMemo(() => {
    const lower = q.toLowerCase();
    return library
      .filter(
        (e) =>
          !lower ||
          e.name.toLowerCase().includes(lower) ||
          e.muscleGroup.toLowerCase().includes(lower) ||
          e.equipment.toLowerCase().includes(lower),
      )
      .slice(0, 50);
  }, [library, q]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 30,
        display: "flex",
        alignItems: "flex-end",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "white",
          width: "100%",
          maxHeight: "85vh",
          overflowY: "auto",
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          padding: 16,
          paddingBottom: "calc(16px + env(safe-area-inset-bottom))",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="row" style={{ marginBottom: 12 }}>
          <h2 className="h2" style={{ margin: 0 }}>Quick start</h2>
          <div className="spacer" />
          <button className="btn small secondary" onClick={onClose}>Close</button>
        </div>

        <div className="filters" style={{ marginBottom: 12 }}>
          <span
            className={`chip ${tab === "programs" ? "active" : ""}`}
            onClick={() => setTab("programs")}
          >
            From a program
          </span>
          <span
            className={`chip ${tab === "plans" ? "active" : ""}`}
            onClick={() => setTab("plans")}
          >
            From a plan
          </span>
          <span
            className={`chip ${tab === "exercise" ? "active" : ""}`}
            onClick={() => setTab("exercise")}
          >
            Single exercise
          </span>
        </div>

        {tab === "programs" && (
          programs.length === 0 ? (
            <div className="card">
              <div className="muted">No programs yet.</div>
              <Link to="/programs" className="btn" style={{ marginTop: 12 }} onClick={onClose}>
                Create a program
              </Link>
            </div>
          ) : (
            <div className="col">
              {programs.map((p) => {
                const len = p.plans.length;
                const idx = len ? p.state.currentIndex % len : 0;
                return (
                  <button
                    key={p.id}
                    className="list-card"
                    disabled={busy || len === 0}
                    onClick={() => onPickProgram(p)}
                  >
                    <div className="thumb">{p.name.charAt(0).toUpperCase()}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700 }}>{p.name}</div>
                      <div className="muted">
                        {len === 0
                          ? "No plans"
                          : `Next: ${tplName(p.plans[idx])} (${idx + 1}/${len})`}
                      </div>
                    </div>
                    <span className="chip">{p.active ? "Active" : "Start"}</span>
                  </button>
                );
              })}
            </div>
          )
        )}

        {tab === "plans" && (
          templates.length === 0 ? (
            <div className="card">
              <div className="muted">You don't have any plans yet.</div>
              <Link to="/programs" className="btn" style={{ marginTop: 12 }} onClick={onClose}>
                Create a plan
              </Link>
            </div>
          ) : (
            <div className="col">
              {templates.map((t) => (
                <button
                  key={t.id}
                  className="list-card"
                  disabled={busy}
                  onClick={() => onPickTemplate(t.id)}
                >
                  <div className="thumb">{t.name.charAt(0).toUpperCase()}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>{t.name}</div>
                    <div className="muted">{t.exercises.length} exercises</div>
                  </div>
                  <span className="chip">Start</span>
                </button>
              ))}
            </div>
          )
        )}

        {tab === "exercise" && (
          <>
            <input
              autoFocus
              placeholder="Search exercises…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <div style={{ marginTop: 12 }}>
              {filteredExercises.length === 0 && (
                <div className="muted">No exercises match.</div>
              )}
              {filteredExercises.map((ex) => (
                <button
                  key={ex.id}
                  className="list-card"
                  disabled={busy}
                  onClick={() => onPickExercise(ex)}
                >
                  <div className="thumb">{ex.name.charAt(0).toUpperCase()}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>{ex.name}</div>
                    <div className="muted">
                      {ex.muscleGroup} · {ex.equipment}
                    </div>
                  </div>
                  <span className="chip">Start</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
